import React, { useMemo } from 'react';
import styled from 'styled-components';
import { Controller, useFormContext } from 'react-hook-form';
import {
  IRoleMetaFieldsFragment,
  ISchemaFieldsFragment,
} from '../../../../../utils/apollo/graphqlSchema';
import {
  FieldErrorHint,
  FormatValueDropdown,
  InputField,
} from '../../../../../components/Form';
import * as InviteUsersModalUI from './InviteUsersModalUI';
import { IInviteUsersModalState } from './types';
import { IReactSelectOption } from '../../../../../utils/types';
import {
  DefaultView,
  defaultViewLabels,
} from '../../../../../utils/userSettings';
import { SquareRemoveButton } from '../../../../../components/Button';

interface IProps {
  userIndex: number;
  roles: IRoleMetaFieldsFragment[];
  schemas: ISchemaFieldsFragment[];
  onRemove: (userIndex: number) => void;
  enableSso: boolean;
  allowRemoveUser: boolean;
}

const LastColumn = styled.div`
  display: grid;
  align-items: center;
  grid-template-columns: 1fr 24px;
  column-gap: 4px;
`;

const FieldWrapper = styled.div`
  display: flex;
  align-items: center;
  column-gap: 2px;
`;

const viewOptions: IReactSelectOption[] = Object.values(DefaultView).map(
  (view) => ({
    value: view,
    label: defaultViewLabels[view],
  }),
);

const UserInvite = ({
  userIndex,
  roles,
  schemas,
  onRemove,
  enableSso,
  allowRemoveUser,
}: IProps) => {
  const { register, control } = useFormContext<IInviteUsersModalState>();

  const roleOptions: IReactSelectOption[] = useMemo(
    () =>
      roles.map((role) => ({
        label: role.name,
        value: role.id,
      })),
    [roles],
  );

  const schemaOptions: IReactSelectOption[] = useMemo(
    () =>
      schemas.map((schema) => ({
        label: schema.name,
        value: schema.id,
      })),
    [schemas],
  );

  return (
    <InviteUsersModalUI.FormRow $enableSso={enableSso}>
      {!enableSso ? (
        <>
          <Controller
            name={`users.${userIndex}.firstName`}
            control={control}
            render={({ field, fieldState }) => (
              <FieldWrapper>
                <InputField
                  {...field}
                  autoComplete="off"
                  placeholder={'Enter First Name'}
                  hasError={Boolean(fieldState.error)}
                />
                {fieldState.error?.message && (
                  <FieldErrorHint errorMessage={fieldState.error.message} />
                )}
              </FieldWrapper>
            )}
          />
          <Controller
            name={`users.${userIndex}.lastName`}
            control={control}
            render={({ field, fieldState }) => (
              <FieldWrapper>
                <InputField
                  {...field}
                  autoComplete="off"
                  placeholder={'Enter Last Name'}
                  hasError={Boolean(fieldState.error)}
                />
                {fieldState.error?.message && (
                  <FieldErrorHint errorMessage={fieldState.error.message} />
                )}
              </FieldWrapper>
            )}
          />
        </>
      ) : (
        <>
          <input {...register(`users.${userIndex}.firstName`)} type="hidden" />
          <input {...register(`users.${userIndex}.lastName`)} type="hidden" />
        </>
      )}
      <Controller
        name={`users.${userIndex}.email`}
        control={control}
        render={({ field, fieldState }) => (
          <FieldWrapper>
            <InputField
              {...field}
              name="search" // prevent safari from auto-filling
              autoComplete="off"
              placeholder={'Enter Email Address'}
              hasError={Boolean(fieldState.error)}
            />
            {fieldState.error?.message && (
              <FieldErrorHint errorMessage={fieldState.error.message} />
            )}
          </FieldWrapper>
        )}
      />
      <Controller
        name={`users.${userIndex}.roleId`}
        control={control}
        render={({ field, fieldState }) => (
          <FieldWrapper>
            <FormatValueDropdown
              value={field.value}
              onChange={field.onChange}
              options={roleOptions}
              placeholder={'Select Role'}
              hasError={Boolean(fieldState.error)}
            />
            {fieldState.error?.message && (
              <FieldErrorHint errorMessage={fieldState.error.message} />
            )}
          </FieldWrapper>
        )}
      />
      <Controller
        name={`users.${userIndex}.defaultSchemaId`}
        control={control}
        render={({ field, fieldState }) => (
          <FieldWrapper>
            <FormatValueDropdown
              value={field.value}
              onChange={field.onChange}
              options={schemaOptions}
              placeholder={'Select'}
              hasError={Boolean(fieldState.error)}
            />
            {fieldState.error?.message && (
              <FieldErrorHint errorMessage={fieldState.error.message} />
            )}
          </FieldWrapper>
        )}
      />
      <LastColumn>
        <Controller
          name={`users.${userIndex}.defaultView`}
          control={control}
          render={({ field, fieldState }) => (
            <FieldWrapper>
              <FormatValueDropdown
                value={field.value}
                onChange={field.onChange}
                options={viewOptions}
                placeholder={'Select'}
                hasError={Boolean(fieldState.error)}
              />
              {fieldState.error?.message && (
                <FieldErrorHint errorMessage={fieldState.error.message} />
              )}
            </FieldWrapper>
          )}
        />
        {allowRemoveUser ? (
          <SquareRemoveButton
            onClick={() => onRemove(userIndex)}
            content={'Remove User'}
          />
        ) : (
          <div />
        )}
      </LastColumn>
      <input {...register(`users.${userIndex}.ssoEnabled`)} type="hidden" />
    </InviteUsersModalUI.FormRow>
  );
};

export default UserInvite;
